import { useCallback, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api';
import { Employee } from '@/types';
import { Download, FileSpreadsheet, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';

const TEMPLATE_HEADERS = [
  'First Name',
  'Surname',
  'Other Name',
  'Email',
  'Phone',
  'Cell Number',
  'NRC',
  'Date of Birth (YYYY-MM-DD)',
  'TPIN',
  'NHIMA Number',
  'Gender (male/female/other)',
  'Man Number',
  'Social Security No',
  'Engagement Date (YYYY-MM-DD)',
  'Department Name',
  'Position Title',
  'Division (DIV I/DIV II/DIV III/DIV IV)',
  'Status (active/inactive/on_leave)',
  'Union Type (ZULAWU/FIRESUZ/Other)',
  'Contribution (LF/NA/PS)',
  'Basic Salary',
  'Bank Name',
  'Bank Branch',
  'Bank Account No',
];

const SAMPLE_ROW: Record<string, string | number> = {
  'First Name': 'John',
  Surname: 'Doe',
  'Other Name': 'Michael',
  Email: 'john.doe@example.com',
  Phone: '0971234567',
  'Cell Number': '0961234567',
  NRC: '123456/78/9',
  'Date of Birth (YYYY-MM-DD)': '1990-05-15',
  TPIN: '123456789',
  'NHIMA Number': 'NH123456',
  'Gender (male/female/other)': 'male',
  'Man Number': 'MAN001',
  'Social Security No': 'SS001',
  'Engagement Date (YYYY-MM-DD)': '2024-01-01',
  'Department Name': 'Finance',
  'Position Title': 'Accountant',
  'Division (DIV I/DIV II/DIV III/DIV IV)': 'DIV I',
  'Status (active/inactive/on_leave)': 'active',
  'Union Type (ZULAWU/FIRESUZ/Other)': 'ZULAWU',
  'Contribution (LF/NA/PS)': 'LF',
  'Basic Salary': 5000,
  'Bank Name': 'ZANACO',
  'Bank Branch': 'Main Branch',
  'Bank Account No': '1234567890',
};

type ParsedRow = Record<string, unknown>;

export type ImportRow = {
  rowNumber: number;
  data: ParsedRow;
  errors: string[];
  valid: boolean;
};

interface BulkImportEmployeesProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
}

function unwrapData<T>(response: unknown): T {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as { data: T }).data;
  }
  return response as T;
}

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function normalizeKey(header: string): string {
  const h = normalizeHeader(header);
  const map: Record<string, string> = {
    'first name': 'firstName',
    surname: 'lastName',
    'last name': 'lastName',
    'other name': 'otherName',
    email: 'email',
    phone: 'phone',
    'cell number': 'cellNumber',
    nrc: 'nrc',
    'date of birth (yyyy-mm-dd)': 'dateOfBirth',
    'date of birth': 'dateOfBirth',
    tpin: 'taxId',
    'nhima number': 'nhimaNumber',
    'gender (male/female/other)': 'gender',
    gender: 'gender',
    'man number': 'manNumber',
    'social security no': 'socialSecurityNo',
    'engagement date (yyyy-mm-dd)': 'engagementDate',
    'engagement date': 'engagementDate',
    'department name': 'departmentName',
    department: 'departmentName',
    'position title': 'positionTitle',
    position: 'positionTitle',
    'division (div i/div ii/div iii/div iv)': 'division',
    division: 'division',
    'status (active/inactive/on_leave)': 'status',
    status: 'status',
    'union type (zulawu/firesuz/other)': 'unionType',
    'union type': 'unionType',
    'contribution (lf/na/ps)': 'contributionType',
    contribution: 'contributionType',
    'basic salary': 'basicSalary',
    'bank name': 'bankName',
    'bank branch': 'bankBranch',
    'bank account no': 'bankAccount',
    'bank account': 'bankAccount',
  };
  return map[h] ?? h;
}

function cellToString(value: unknown): string {
  if (value == null || value === '') return '';
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === 'number' && value > 20000 && value < 60000) {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (parsed) {
      const y = parsed.y;
      const m = String(parsed.m).padStart(2, '0');
      const d = String(parsed.d).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  return String(value).trim();
}

function mapRawRow(raw: Record<string, unknown>): ParsedRow {
  const mapped: ParsedRow = {};
  for (const [header, value] of Object.entries(raw)) {
    if (!header || header.startsWith('__EMPTY')) continue;
    const key = normalizeKey(header);
    mapped[key] = value;
  }
  return mapped;
}

function validateRow(row: ParsedRow): string[] {
  const errors: string[] = [];
  const req = (key: string, label: string) => {
    const v = cellToString(row[key]);
    if (!v) errors.push(`Missing required: ${label}`);
  };

  req('firstName', 'First Name');
  req('lastName', 'Surname');
  req('email', 'Email');
  req('nrc', 'NRC');
  req('manNumber', 'Man Number');
  req('departmentName', 'Department Name');
  req('positionTitle', 'Position Title');

  const gender = cellToString(row.gender).toLowerCase();
  if (!gender) {
    errors.push('Missing required: Gender');
  } else if (!['male', 'female', 'other'].includes(gender)) {
    errors.push('Gender must be male, female, or other');
  }

  const status = cellToString(row.status).toLowerCase() || 'active';
  if (!['active', 'inactive', 'on_leave'].includes(status)) {
    errors.push('Status must be active, inactive, or on_leave');
  }

  const email = cellToString(row.email);
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.push('Invalid email format');
  }

  const salary = cellToString(row.basicSalary);
  if (salary && Number.isNaN(Number(salary))) {
    errors.push('Basic Salary must be a number');
  }

  return errors;
}

function isRowEmpty(row: ParsedRow): boolean {
  return Object.values(row).every((v) => cellToString(v) === '');
}

export const BulkImportEmployees = ({
  open,
  onOpenChange,
  onImportComplete,
}: BulkImportEmployeesProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  const validRows = useMemo(() => rows.filter((r) => r.valid), [rows]);
  const invalidRows = useMemo(() => rows.filter((r) => !r.valid), [rows]);

  const reset = useCallback(() => {
    setRows([]);
    setFileName(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet([SAMPLE_ROW], { header: TEMPLATE_HEADERS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.writeFile(wb, 'employee_import_template.xlsx');
  };

  const parseFile = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) {
      toast({ title: 'Invalid file', description: 'No worksheet found.', variant: 'destructive' });
      return;
    }
    const sheet = workbook.Sheets[sheetName];
    const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
    const parsed: ImportRow[] = [];
    json.forEach((raw, index) => {
      const mapped = mapRawRow(raw);
      if (isRowEmpty(mapped)) return;
      const errors = validateRow(mapped);
      parsed.push({
        rowNumber: index + 2,
        data: mapped,
        errors,
        valid: errors.length === 0,
      });
    });
    setRows(parsed);
    setFileName(file.name);
    if (parsed.length === 0) {
      toast({
        title: 'No data rows',
        description: 'The file has no employee rows to import.',
        variant: 'destructive',
      });
    }
  };

  const resolveDepartmentId = async (
    name: string,
    cache: Map<string, number>,
    departments: { id: number; name: string }[]
  ): Promise<number> => {
    const key = name.trim().toLowerCase();
    if (cache.has(key)) return cache.get(key)!;
    const existing = departments.find((d) => d.name?.trim().toLowerCase() === key);
    if (existing) {
      cache.set(key, existing.id);
      return existing.id;
    }
    const created = await apiService.createDepartment({
      name: name.trim(),
      cid: user?.cid ?? null,
    });
    const payload = unwrapData<{ id: number; name?: string } | { id: number }[]>(created);
    const dept = Array.isArray(payload) ? payload[0] : payload;
    const id = Number(dept?.id);
    if (!id) throw new Error(`Could not create department "${name}"`);
    departments.push({ id, name: name.trim() });
    cache.set(key, id);
    return id;
  };

  const resolvePositionId = async (
    title: string,
    departmentId: number,
    cache: Map<string, number>,
    positions: { id: number; title: string; department_id?: number }[]
  ): Promise<number> => {
    const key = `${departmentId}::${title.trim().toLowerCase()}`;
    if (cache.has(key)) return cache.get(key)!;
    const existing = positions.find(
      (p) =>
        p.title?.trim().toLowerCase() === title.trim().toLowerCase() &&
        Number(p.department_id) === departmentId
    );
    if (existing) {
      cache.set(key, existing.id);
      return existing.id;
    }
    const created = await apiService.createPosition({
      title: title.trim(),
      department_id: departmentId,
    });
    const payload = unwrapData<{ id: number; title?: string; department_id?: number } | { id: number }[]>(
      created
    );
    const pos = Array.isArray(payload) ? payload[0] : payload;
    const id = Number(pos?.id);
    if (!id) throw new Error(`Could not create position "${title}"`);
    positions.push({ id, title: title.trim(), department_id: departmentId });
    cache.set(key, id);
    return id;
  };

  const handleImport = async () => {
    if (validRows.length === 0) return;
    if (user?.cid == null) {
      toast({ title: 'Council required', description: 'Sign in with a council account.', variant: 'destructive' });
      return;
    }

    setImporting(true);
    let success = 0;
    let failed = 0;
    const deptCache = new Map<string, number>();
    const posCache = new Map<string, number>();

    try {
      const [deptRes, posRes] = await Promise.all([
        apiService.getDepartments(user.cid),
        apiService.getPositions(user.cid),
      ]);
      const departments = (unwrapData(deptRes) as { id: number; name: string }[]) ?? [];
      const positions = (unwrapData(posRes) as { id: number; title: string; department_id?: number }[]) ?? [];

      for (const row of validRows) {
        try {
          const d = row.data;
          const departmentId = await resolveDepartmentId(
            cellToString(d.departmentName),
            deptCache,
            Array.isArray(departments) ? departments : []
          );
          const positionId = await resolvePositionId(
            cellToString(d.positionTitle),
            departmentId,
            posCache,
            Array.isArray(positions) ? positions : []
          );

          const manNumber = cellToString(d.manNumber);
          const employee: Employee = {
            id: `emp-${Date.now()}-${row.rowNumber}`,
            userId: `user-${Date.now()}-${row.rowNumber}`,
            employeeId: manNumber,
            firstName: cellToString(d.firstName),
            lastName: cellToString(d.lastName),
            otherName: cellToString(d.otherName) || undefined,
            email: cellToString(d.email),
            phone: cellToString(d.phone) || cellToString(d.cellNumber) || '',
            cellNumber: cellToString(d.cellNumber) || undefined,
            position_id: positionId,
            department_id: departmentId,
            division: cellToString(d.division) || undefined,
            joinDate: cellToString(d.engagementDate) || undefined,
            engagementDate: cellToString(d.engagementDate) || undefined,
            dateOfBirth: cellToString(d.dateOfBirth) || undefined,
            gender: (cellToString(d.gender).toLowerCase() || 'male') as Employee['gender'],
            socialSecurityNo: cellToString(d.socialSecurityNo) || undefined,
            status: (cellToString(d.status).toLowerCase() || 'active') as Employee['status'],
            basicSalary: Number(cellToString(d.basicSalary)) || 0,
            bankName: cellToString(d.bankName) || undefined,
            bankAccount: cellToString(d.bankAccount) || undefined,
            bankBranch: cellToString(d.bankBranch) || undefined,
            manNumber,
            nrc: cellToString(d.nrc),
            taxId: cellToString(d.taxId) || undefined,
            nhimaNumber: cellToString(d.nhimaNumber) || undefined,
            unionType: cellToString(d.unionType) || undefined,
            contributionType: cellToString(d.contributionType) || undefined,
            cid: user.cid,
          };

          await apiService.createEmployee(employee);
          success += 1;
        } catch (err) {
          console.error('Import row failed', row.rowNumber, err);
          failed += 1;
        }
      }

      toast({
        title: 'Import complete',
        description: `${success} employee(s) imported${failed ? `, ${failed} failed` : ''}.`,
      });
      if (success > 0) {
        onImportComplete?.();
        handleOpenChange(false);
      }
    } catch (error) {
      console.error('Bulk import failed', error);
      toast({
        title: 'Import failed',
        description: 'Could not complete bulk import. Check console for details.',
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Employees</DialogTitle>
          <DialogDescription>
            Upload an Excel file using the template. Departments and positions are created automatically if missing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>

          <div
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <FileSpreadsheet className="h-10 w-10 mx-auto text-gray-400 mb-2" />
            <p className="text-sm font-medium">
              {fileName ? fileName : 'Click to upload Excel file (.xlsx, .xls)'}
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) parseFile(file);
              }}
            />
          </div>

          {rows.length > 0 && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  {validRows.length} valid
                </Badge>
                {invalidRows.length > 0 && (
                  <Badge variant="destructive" className="gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {invalidRows.length} with errors
                  </Badge>
                )}
              </div>

              {invalidRows.length > 0 && (
                <div className="max-h-40 overflow-y-auto rounded border p-3 text-sm space-y-2 bg-red-50/50">
                  {invalidRows.slice(0, 15).map((row) => (
                    <div key={row.rowNumber}>
                      <span className="font-medium">Row {row.rowNumber}:</span>{' '}
                      {row.errors.join('; ')}
                    </div>
                  ))}
                  {invalidRows.length > 15 && (
                    <p className="text-gray-500">…and {invalidRows.length - 15} more rows with errors</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={importing}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-payroll-primary hover:bg-blue-700"
            disabled={validRows.length === 0 || importing}
            onClick={handleImport}
          >
            <Upload className="h-4 w-4 mr-2" />
            {importing
              ? 'Importing…'
              : `Import ${validRows.length} Employee${validRows.length === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
